import * as Sentry from '@sentry/browser';

/**
 * Configures and initializes Sentry for browser environments.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.dsn - Sentry DSN (required)
 * @param {string} options.env - Environment name (e.g., 'development', 'production')
 * @param {string} options.release - Release version identifier
 * @param {string} [options.userId] - User ID to associate with events
 * @param {number} [options.tracesSampleRate=0.4] - Sample rate for performance traces (0.0 to 1.0)
 * @returns {Object} Sentry instance
 * @throws {Error} If DSN is not provided or invalid
 */
export default function ({
  dsn,
  env,
  release,
  userId,
  tracesSampleRate = 0.4
}) {
  if (!dsn)
    throw new Error('Sentry DSN is required');

  if (typeof dsn !== 'string' || dsn.trim().length === 0)
    throw new Error('Sentry DSN must be a non-empty string');

  if (tracesSampleRate !== undefined && (typeof tracesSampleRate !== 'number' || tracesSampleRate < 0 || tracesSampleRate > 1))
    throw new Error('tracesSampleRate must be a number between 0 and 1');

  try {
    Sentry.init({
      dsn,
      environment: env,
      release,
      integrations: [
        Sentry.browserTracingIntegration({
          tracePropagationTargets: ['*']
        }),
        Sentry.browserApiErrorsIntegration(),
        Sentry.extraErrorDataIntegration(),
        Sentry.captureConsoleIntegration(),
        Sentry.contextLinesIntegration(),
        Sentry.httpClientIntegration()
      ],
      tracesSampleRate
    });

    if (userId)
      Sentry.setUser({ id: userId });
  }
  catch (error) {
    console.error('Failed to initialize Sentry:', error);
    throw error;
  }

  return Sentry;
}
